import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  List,
  ListItem,
  Avatar,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Stack,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  Print as PrintIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';

// Helper functions for wholesale cart management
const getWholesaleCartItems = () => {
  const cartStr = localStorage.getItem("wholesaleCart");
  if (!cartStr) return [];
  try {
    return JSON.parse(cartStr);
  } catch (error) {
    console.error("Error parsing wholesale cart data:", error);
    return [];
  }
};

const saveWholesaleCartItems = (items) => {
  localStorage.setItem("wholesaleCart", JSON.stringify(items));
};

const getCurrentUser = () => {
  const userStr = localStorage.getItem("user");
  if (!userStr) return null;
  try {
    const user = JSON.parse(userStr);
    return { 
      id: user.id || null,
      name: user.name || user.username || '', 
      username: user.username || '',
      phone: user.phone || '',
      address: user.address || '',
      city: user.city || '',
      points: user.points || 0,
      role: user.role || 'customer'
    };
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

const WholesaleCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [cartItems, setCartItems] = useState(() => {
    // Get cart items from location state or localStorage
    const stateItems = location.state?.cartItems;
    return stateItems && stateItems.length > 0 ? stateItems : getWholesaleCartItems();
  });
  const [user, setUser] = useState(getCurrentUser());
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // Form states
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    phone: '',
    address: '',
    city: '',
    notes: ''
  });
  
  const [paymentMethod, setPaymentMethod] = useState('cash'); // cash, credit
  const [deliveryMethod, setDeliveryMethod] = useState('mediator'); // mediator, transport
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderCode, setOrderCode] = useState(null);
  const [transferReceipt, setTransferReceipt] = useState(null);
  const [transferReceiptPreview, setTransferReceiptPreview] = useState(null);

  // Cities list with proper mapping (same as regular checkout)
  const cities = [
    { value: 'Baghdad', label: 'بغداد' },
    { value: 'Basra', label: 'البصرة' },
    { value: 'Erbil', label: 'أربيل' },
    { value: 'Najaf', label: 'النجف' },
    { value: 'Karbala', label: 'كربلاء' },
    { value: 'Sulaymaniyah', label: 'السليمانية' },
    { value: 'Mosul', label: 'الموصل' },
    { value: 'Duhok', label: 'دهوك' },
    { value: 'Babylon', label: 'بابل' },
    { value: 'Kirkuk', label: 'كركوك' },
    { value: 'DhiQar', label: 'ذي قار' },
    { value: 'Muthanna', label: 'المثنى' },
    { value: 'Qadisiyyah', label: 'القادسية' },
    { value: 'Maysan', label: 'ميسان' },
    { value: 'Saladin', label: 'صلاح الدين' },
    { value: 'Diyala', label: 'ديالى' },
    { value: 'Anbar', label: 'الأنبار' },
    { value: 'Wasit', label: 'واسط' },
    { value: 'Other', label: 'مدينة أخرى' }
  ];

  const mapCityToDropdownValue = (cityName) => {
    if (!cityName) return '';
    
    const cityMapping = {
      // Arabic database values to English dropdown values
      'بغداد': 'Baghdad',
      'البصرة': 'Basra', 
      'أربيل': 'Erbil',
      'النجف': 'Najaf',
      'كربلاء': 'Karbala',
      'السليمانية': 'Sulaymaniyah',
      'الموصل': 'Mosul',
      'دهوك': 'Duhok',
      'بابل': 'Babylon',
      'كركوك': 'Kirkuk',
      'ذي قار': 'DhiQar',
      'المثنى': 'Muthanna',
      'القادسية': 'Qadisiyyah',
      'ميسان': 'Maysan',

      // Additional Iraqi cities
      'صلاح الدين': 'Saladin',
      'ديالى': 'Diyala',
      'الأنبار': 'Anbar',
      'واسط': 'Wasit',
      
      // Variations without الـ prefix
      'نجف': 'Najaf',
      'بصرة': 'Basra',
      'موصل': 'Mosul',
      'أنبار': 'Anbar',
      'مثنى': 'Muthanna',
      'قادسية': 'Qadisiyyah',
      
      // Keep English values as-is
      'Baghdad': 'Baghdad',
      'Basra': 'Basra',
      'Erbil': 'Erbil', 
      'Najaf': 'Najaf',
      'Karbala': 'Karbala',
      'Sulaymaniyah': 'Sulaymaniyah',
      'Mosul': 'Mosul',
      'Duhok': 'Duhok',
      'Babylon': 'Babylon',
      'Kirkuk': 'Kirkuk',
      'DhiQar': 'DhiQar',
      'Muthanna': 'Muthanna',
      'Qadisiyyah': 'Qadisiyyah',
      'Maysan': 'Maysan',
      'Saladin': 'Saladin',
      'Diyala': 'Diyala',
      'Anbar': 'Anbar',
      'Wasit': 'Wasit'
    };
    
    // Try exact match first
    if (cityMapping[cityName]) {
      return cityMapping[cityName];
    }
    
    // Try with trimmed spaces
    const trimmedCityName = cityName.trim();
    if (cityMapping[trimmedCityName]) {
      return cityMapping[trimmedCityName];
    }
    
    // If no match found, return the original value (will be handled as custom city)
    console.log('City not found in mapping:', cityName);
    return cityName;
  };

  // Helper function to convert English city names back to Arabic for display
  const mapCityToArabic = (cityName) => {
    if (!cityName) return '';
    
    const cityToArabicMapping = {
      // English to Arabic mapping
      'Baghdad': 'بغداد',
      'Basra': 'البصرة', 
      'Erbil': 'أربيل',
      'Najaf': 'النجف',
      'Karbala': 'كربلاء',
      'Sulaymaniyah': 'السليمانية',
      'Mosul': 'الموصل',
      'Duhok': 'دهوك',
      'Babylon': 'بابل',
      'Kirkuk': 'كركوك',
      'DhiQar': 'ذي قار',
      'Muthanna': 'المثنى',
      'Qadisiyyah': 'القادسية',
      'Maysan': 'ميسان',
      'Saladin': 'صلاح الدين',
      'Diyala': 'ديالى',
      'Anbar': 'الأنبار',
      'Wasit': 'واسط'
    };
    
    // If it's already in Arabic or has mapping, return the Arabic version
    if (cityToArabicMapping[cityName]) {
      return cityToArabicMapping[cityName];
    }
    
    // If no mapping found, return the original value (custom cities)
    return cityName;
  };

  // Stepper steps
  const steps = ['بيانات الشحن', 'طريقة التوصيل', 'مراجعة الطلب', 'طريقة الدفع', 'تأكيد الطلب'];

  // Check cart and initialize shipping info
  useEffect(() => {
    if (cartItems.length === 0 && !orderPlaced) {
      showMessage('سلة الجملة فارغة', 'warning');
      navigate('/wholesale');
      return;
    }

    // Initialize shipping info with user data
    if (user) {
      const mappedCity = mapCityToDropdownValue(user?.city);
      setShippingInfo(prevShippingInfo => ({
        fullName: user?.name || prevShippingInfo.fullName,
        phone: user?.phone || prevShippingInfo.phone,
        address: user?.address || prevShippingInfo.address,
        city: mappedCity || prevShippingInfo.city,
        notes: prevShippingInfo.notes
      }));
    }
  }, [user, cartItems, orderPlaced, navigate]);

  // Auto-select payment method based on delivery method
  useEffect(() => {
    if (deliveryMethod === 'transport') {
      setPaymentMethod('credit');
    }
  }, [deliveryMethod]);

  const showMessage = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-IQ').format(amount);
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const updatedItems = cartItems.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updatedItems);
    saveWholesaleCartItems(updatedItems);
  };

  const removeFromCart = (productId) => {
    const updatedItems = cartItems.filter(item => item.id !== productId);
    setCartItems(updatedItems);
    saveWholesaleCartItems(updatedItems);
    showMessage('تم حذف المنتج من السلة');
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalWeight = () => {
    return cartItems.reduce((total, item) => total + ((item.weight || 0) * item.quantity), 0);
  };

  // Wholesale delivery cost calculation (higher minimum for wholesale)
  const getDeliveryCost = () => {
    // For transport delivery method, don't calculate delivery price
    if (deliveryMethod === 'transport') {
      return 0;
    }
    
    const totalWeight = getTotalWeight();
    const baseDeliveryCost = 10000; // 10,000 IQD base for wholesale
    const additionalCost = Math.max(0, totalWeight - 10) * 1500; // 1,500 IQD per kg above 10kg
    return baseDeliveryCost + additionalCost;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showMessage('يرجى رفع صورة فقط', 'error');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showMessage('حجم الملف يجب أن يكون أقل من 5 ميجابايت', 'error');
        return;
      }

      // Store the file object
      setTransferReceipt(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setTransferReceiptPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      showMessage('تم رفع الإيصال بنجاح', 'success');
    }
  };

  const removeReceipt = () => {
    setTransferReceipt(null);
    setTransferReceiptPreview(null);
    // Reset file input
    const fileInput = document.getElementById('transfer-receipt-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateShippingInfo = () => {
    const { fullName, phone, address, city } = shippingInfo;
    if (!fullName || !phone || !address || !city) {
      showMessage('يرجى ملء جميع الحقول المطلوبة', 'error');
      return false;
    }
    if (!/^[0-9+\-\s]{10,}$/.test(phone)) {
      showMessage('يرجى إدخال رقم هاتف صحيح', 'error');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateShippingInfo()) {
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handlePlaceOrder = async () => {
    if (!validateShippingInfo()) {
      return;
    }

    if (cartItems.length === 0) {
      showMessage('السلة فارغة', 'error');
      return;
    }

    // Validate transfer receipt for card payment
    if (paymentMethod === 'credit' && !transferReceipt) {
      showMessage('يرجى رفع صورة إيصال التحويل', 'error');
      return;
    }

    setLoading(true);
    try {
      // Convert receipt file to base64 if it exists
      let receiptBase64 = null;
      if (transferReceipt) {
        receiptBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(transferReceipt);
        });
      }

      const orderData = {
        user_id: user?.id || null,
        customer_name: shippingInfo.fullName,
        phone: shippingInfo.phone,
        address: shippingInfo.address,
        city: mapCityToArabic(shippingInfo.city), // Convert to Arabic for backend storage
        notes: shippingInfo.notes,
        payment_method: paymentMethod,
        delivery_method: deliveryMethod,
        order_type: 'wholesale',
        items: cartItems.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name,
          sku: item.sku || item.SKU || '',
          brand_name: item.brand || ''
        })),
        delivery_cost: getDeliveryCost(),
        receipt_base64: receiptBase64 // Add receipt data as base64 if available
      };

      const response = await fetch(`${API_BASE_URL}/api/wholesale_checkout.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setOrderId(result.order_id);
        setOrderCode(result.order_code);
        setOrderPlaced(true);
        
        // Clear wholesale cart
        localStorage.removeItem('wholesaleCart');
        setCartItems([]);
        
        // Dispatch event for header update
        window.dispatchEvent(new CustomEvent('wholesaleCartUpdated', { 
          detail: { cartItems: [], cartLength: 0 }
        }));
        
        showMessage('تم تأكيد طلب الجملة بنجاح! ستصلك رسالة واتساب بالتأكيد', 'success');
        setActiveStep(4);
      } else {
        throw new Error(result.message || 'فشل في تأكيد الطلب');
      }
    } catch (error) {
      console.error('Order placement error:', error);
      showMessage(error.message || 'حدث خطأ أثناء تأكيد الطلب', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Print receipt function (same as regular checkout)
  const handlePrintReceipt = () => {
    if (!user?.id) {
      showMessage('معرف المستخدم غير متاح', 'error');
      return;
    }
    
    // Ask user to input order code
    const orderCodeInput = prompt('يرجى إدخال رقم الطلب:', orderCode || '');
    
    if (!orderCodeInput || orderCodeInput.trim() === '') {
      showMessage('يرجى إدخال رقم الطلب', 'error');
      return;
    }
    
    const printUrl = `${API_BASE_URL}/api/order_pdf.php?order_code=${encodeURIComponent(orderCodeInput.trim())}&user_id=${encodeURIComponent(user.id)}`;
    
    // Open PDF in a new tab for printing
    window.open(printUrl, '_blank');
    showMessage('جاري فتح الفاتورة للطباعة...', 'info');
  };

  // Render cart review step
const renderCartReview = () => (
    <Card sx={{ mb: 3 }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: '#2E7D32' }} />
                <Typography variant="h6">مراجعة طلب الجملة</Typography>
            </Box>
            
            <List sx={{ p: 0 }}>
                {cartItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                        <ListItem sx={{ px: 0, py: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'center', sm: 'center' }, 
                                width: '100%', 
                                gap: { xs: 1, sm: 2 } 
                            }}>
                                <Avatar
                                    src={item.image && typeof item.image === 'string' && item.image.trim() ? 
                                        (item.image.startsWith('http') ? 
                                            item.image : 
                                            item.image.startsWith('api/uploads/products/') ? 
                                                `${API_BASE_URL}/${item.image}` : 
                                                `https://al-sadik.com/api/uploads/products/${item.image}`
                                        ) : '/placeholder-image.jpg'
                                    }
                                    variant="rounded"
                                    sx={{ width: { xs: 120, sm: 80 }, height: { xs: 120, sm: 80 } }}
                                />
                                
                                <Box sx={{ 
                                    flex: 1, 
                                    textAlign: { xs: 'center', sm: 'left' },
                                    minWidth: 0
                                }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        {item.name}
                                    </Typography>
                                    <Chip 
                                        label="جملة" 
                                        size="small" 
                                        sx={{ 
                                            backgroundColor: '#2E7D32', 
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            mb: 1
                                        }} 
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        سعر الوحدة: {formatCurrency(item.price)} د.ع
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'row', sm: 'row' },
                                    alignItems: 'center', 
                                    gap: { xs: 2, sm: 1 },
                                    justifyContent: { xs: 'space-between', sm: 'flex-end' },
                                    width: { xs: '100%', sm: 'auto' },
                                    mt: { xs: 2, sm: 0 }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton 
                                            size="small"
                                            onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                                            disabled={item.quantity <= 1}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                        <Typography sx={{ mx: 1, minWidth: '30px', textAlign: 'center' }}>
                                            {item.quantity}
                                        </Typography>
                                        <IconButton 
                                            size="small"
                                            onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </Box>
                                    
                                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' }, minWidth: { xs: 'auto', sm: 120 } }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            {formatCurrency(item.price * item.quantity)} د.ع
                                        </Typography>
                                    </Box>
                                    
                                    <IconButton 
                                        onClick={() => removeFromCart(item.id)}
                                        size="small"
                                        sx={{ color: 'error.main' }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </ListItem>
                        {index < cartItems.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Order Summary */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        المجموع الفرعي ({getTotalItems()} قطعة):
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        {formatCurrency(getTotalPrice())} د.ع
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        تكلفة الشحن:
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        {formatCurrency(getDeliveryCost())} د.ع
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        الإجمالي:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        {formatCurrency(getTotalPrice() + getDeliveryCost())} د.ع
                    </Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

  // Render shipping info step
  const renderShippingInfo = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <LocationIcon sx={{ mr: 1, color: '#2E7D32' }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            بيانات الشحن
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="الاسم الكامل *"
              value={shippingInfo.fullName}
              onChange={(e) => setShippingInfo(prev => ({ ...prev, fullName: e.target.value }))}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="رقم الهاتف *"
              value={shippingInfo.phone}
              onChange={(e) => setShippingInfo(prev => ({ ...prev, phone: e.target.value }))}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="العنوان *"
              value={shippingInfo.address}
              onChange={(e) => setShippingInfo(prev => ({ ...prev, address: e.target.value }))}
              variant="outlined"
              multiline
              rows={isMobile ? 2 : 2}
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size={isMobile ? 'small' : 'medium'}>
              <InputLabel>المحافظة *</InputLabel>
              <Select
                value={shippingInfo.city}
                onChange={(e) => setShippingInfo(prev => ({ ...prev, city: e.target.value }))}
                label="المحافظة *"
              >
                {cities.map((city) => (
                  <MenuItem key={city.value} value={city.value}>
                    {city.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Show text field for custom city if "Other" is selected or city doesn't match dropdown */}
          {(shippingInfo.city === 'Other' || (shippingInfo.city && !cities.some(city => city.value === shippingInfo.city))) && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="اسم المدينة"
                value={shippingInfo.city === 'Other' ? '' : shippingInfo.city}
                onChange={(e) => setShippingInfo(prev => ({ ...prev, city: e.target.value }))}
                variant="outlined"
                placeholder="أدخل اسم المدينة"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ملاحظات إضافية"
              value={shippingInfo.notes}
              onChange={(e) => setShippingInfo(prev => ({ ...prev, notes: e.target.value }))}
              variant="outlined"
              multiline
              rows={isMobile ? 2 : 3}
              placeholder="ملاحظات خاصة بالطلب أو التوصيل..."
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Render delivery method step
  const renderDeliveryMethod = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
          <ShippingIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: '#2E7D32', mr: { xs: 1, sm: 2 } }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            طريقة التوصيل
          </Typography>
        </Box>
        
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            اختر طريقة التوصيل المناسبة لك
          </FormLabel>
          <RadioGroup
            value={deliveryMethod}
            onChange={(e) => setDeliveryMethod(e.target.value)}
            sx={{ gap: { xs: 1, sm: 2 } }}
          >
            <FormControlLabel
              value="mediator"
              control={<Radio sx={{ color: '#2E7D32', '&.Mui-checked': { color: '#2E7D32' } }} />}
              label={
                <Box sx={{ ml: { xs: 0.5, sm: 1 }, flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    الوسيط
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      lineHeight: 1.3,
                      wordBreak: 'break-word'
                    }}
                  >
                    التوصيل عبر شركة الوسيط 
                  </Typography>
                </Box>
              }
              sx={{ 
                mb: { xs: 1, sm: 2 }, 
                p: { xs: 1, sm: 1.5 }, 
                border: deliveryMethod === 'mediator' ? '2px solid #2E7D32' : '1px solid #e0e0e0',
                borderRadius: 2,
                backgroundColor: deliveryMethod === 'mediator' ? '#f1f8e9' : 'transparent',
                margin: 0,
                width: '100%',
                alignItems: 'flex-start'
              }}
            />
            <FormControlLabel
              value="transport"
              control={<Radio sx={{ color: '#2E7D32', '&.Mui-checked': { color: '#2E7D32' } }} />}
              label={
                <Box sx={{ ml: { xs: 0.5, sm: 1 }, flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    عبر النقليات
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      lineHeight: 1.3,
                      wordBreak: 'break-word'
                    }}
                  >
                    الدفع مقدما  - سعر التوصيل يحدد من قبل شركة النقل عند التوصيل
                  </Typography>
                </Box>
              }
              sx={{ 
                p: { xs: 1, sm: 1.5 }, 
                border: deliveryMethod === 'transport' ? '2px solid #2E7D32' : '1px solid #e0e0e0',
                borderRadius: 2,
                backgroundColor: deliveryMethod === 'transport' ? '#f1f8e9' : 'transparent',
                margin: 0,
                width: '100%',
                alignItems: 'flex-start'
              }}
            />
          </RadioGroup>
        </FormControl>
      </CardContent>
    </Card>
  );

  // Render payment method step
  const renderPaymentMethod = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PaymentIcon sx={{ mr: 1, color: '#2E7D32' }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            طريقة الدفع
          </Typography>
        </Box>
        
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            اختر طريقة الدفع
          </FormLabel>
          <RadioGroup
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            sx={{ mt: 2 }}
          >
            {/* Show cash payment only if delivery method is not transport */}
            {deliveryMethod !== 'transport' && (
              <FormControlLabel
                value="cash"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      الدفع عند الاستلام
                    </Typography>
                    <Chip label="متاح" size="small" color="success" sx={{ ml: 1 }} />
                  </Box>
                }
              />
            )}
            <FormControlLabel
              value="credit"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {deliveryMethod === 'transport' 
                      ? 'الدفع عبر ماستر الرافدين (مطلوب للنقليات)' 
                      : 'الدفع عبر ماستر الرافدين او زين كاش'
                    }
                  </Typography>
                  <Chip 
                    label={deliveryMethod === 'transport' ? 'مطلوب' : 'متاح'} 
                    size="small" 
                    color={deliveryMethod === 'transport' ? 'warning' : 'success'} 
                    sx={{ ml: 1 }} 
                  />
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {paymentMethod === 'credit' && (
          <Box sx={{ mt: 3, p: { xs: 2, sm: 3 }, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#2E7D32', fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              بيانات التحويل:
            </Typography>
            
            {deliveryMethod === 'transport' && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  <strong>ملاحظة:</strong> عند اختيار التوصيل عبر النقليات، يجب الدفع مقدماً عبر ماستر الرافدين فقط.
                </Typography>
              </Alert>
            )}
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                التحويل ماستر الرافدين:
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                <strong>رقم الحساب:</strong> 4496588940
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                <strong>أو رقم الهاتف:</strong> 07813438118
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                <strong>اسم المستلم:</strong> باقر محمد علي العذاري
              </Typography>
            </Box>

            {deliveryMethod !== 'transport' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    التحويل زين كاش (بديل):
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    <strong>رقم الهاتف:</strong> 07813438118
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    <strong>اسم المستلم:</strong> باقر محمد علي العذاري
                  </Typography>
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              رفع إيصال التحويل:
            </Typography>

            {!transferReceiptPreview ? (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: { xs: 3, sm: 4 }, 
                  border: '2px dashed #2E7D32', 
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover': {
                    backgroundColor: '#e8f5e8',
                    borderColor: '#1B5E20'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <input
                  id="transfer-receipt-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="transfer-receipt-upload" style={{ cursor: 'pointer' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box 
                      sx={{ 
                        width: { xs: 50, sm: 60 }, 
                        height: { xs: 50, sm: 60 }, 
                        borderRadius: '50%', 
                        backgroundColor: '#2E7D32', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}
                    >
                      <AddIcon sx={{ color: 'white', fontSize: { xs: 25, sm: 30 } }} />
                    </Box>
                    <Button
                      variant="contained"
                      component="span"
                      sx={{ 
                        backgroundColor: '#2E7D32',
                        '&:hover': { backgroundColor: '#1B5E20' },
                        px: { xs: 3, sm: 4 },
                        py: { xs: 1, sm: 1.5 },
                        fontSize: { xs: '0.9rem', sm: '1.1rem' }
                      }}
                    >
                      اختيار صورة الإيصال
                    </Button>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      اضغط لرفع صورة واضحة لإيصال التحويل
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      الحد الأقصى للحجم: 5 ميجابايت | الأنواع المدعومة: JPG, PNG, GIF
                    </Typography>
                  </Box>
                </label>
              </Box>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                p: { xs: 2, sm: 3 }, 
                border: '2px solid #2E7D32', 
                borderRadius: 2,
                backgroundColor: '#f1f8e9'
              }}>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                  <img
                    src={transferReceiptPreview}
                    alt="إيصال التحويل"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '2px solid #2E7D32',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                  <IconButton
                    onClick={removeReceipt}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: '#f44336',
                      color: 'white',
                      '&:hover': { backgroundColor: '#d32f2f' },
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                  <CheckIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: '#2E7D32', fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    تم رفع الإيصال بنجاح
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    const fileInput = document.getElementById('transfer-receipt-upload');
                    if (fileInput) {
                      fileInput.click();
                    }
                  }}
                  sx={{ 
                    borderColor: '#2E7D32', 
                    color: '#2E7D32',
                    '&:hover': { 
                      borderColor: '#1B5E20', 
                      backgroundColor: '#f1f8e9' 
                    },
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                  }}
                >
                  تغيير الصورة
                </Button>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                يرجى التحويل بالمبلغ الإجمالي: <strong>{formatCurrency(getTotalPrice() + getDeliveryCost())} د.ع</strong> ثم رفع صورة الإيصال.
              </Typography>
            </Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Render order confirmation
  const renderOrderConfirmation = () => (
    <Paper 
      elevation={0} 
      sx={{ 
      p: 4, 
      textAlign: 'center', 
      border: '2px solid', 
      borderColor: 'success.main', 
      backgroundColor: 'white', 
      borderRadius: 3,
      boxShadow: 3
      }}
    >
      <CheckIcon sx={{ fontSize: 100, color: 'success.main', mb: 3 }} />
      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3, color: 'success.main' }}>
      تم تأكيد طلب الجملة بنجاح!
      </Typography>
      
      {/* Order Code Display */}
      <Box sx={{ 
      backgroundColor: '#f8f9fa', 
      border: '3px dashed', 
      borderColor: 'primary.main', 
      borderRadius: 2, 
      p: 3, 
      mb: 3,
      boxShadow: 2
      }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
        كود الطلب: #{orderCode || 'غير متاح'}
      </Typography>
      
      <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
        ستحتاج إلى كود الطلب هذا لمتابعة حالة طلبك والاستفسار عنه
      </Typography>
      </Box>

      {/* WhatsApp Notification Info */}
      <Alert severity="success" sx={{ mb: 3, textAlign: 'right' }}>
      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
        📱 تم إرسال تأكيد الطلب على واتساب
      </Typography>
      <Typography variant="body2">
        ستصلك رسالة على واتساب تحتوي على رقم طلبك وتفاصيل التوصيل المتوقع
      </Typography>
      </Alert>

      {/* Wholesale Order Info */}
      <Alert severity="info" sx={{ mb: 3, textAlign: 'right', backgroundColor: '#e3f2fd' }}>
      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
        🏪 طلب جملة - معالجة خاصة
      </Typography>
      <Typography variant="body2">
        سيتم مراجعة طلب الجملة والتواصل معكم لتأكيد الكميات والأسعار والتسليم
      </Typography>
      </Alert>
      
      {/* Action Buttons */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 3 }}>
      <Button
        variant="contained"
        size="large"
        startIcon={<StoreIcon />}
        onClick={() => navigate('/wholesale')}
        sx={{
        backgroundColor: '#2E7D32',
        color: 'white',
        fontSize: '1.1rem',
        px: 4,
        py: 2,
        '&:hover': {
          backgroundColor: '#1B5E20',
        }
        }}
      >
        العودة للمتجر
      </Button>
      
      <Button
        variant="outlined"
        size="large"
        startIcon={<PrintIcon />}
        onClick={() => {
        if (orderCode && user?.id) {
          const printUrl = `${API_BASE_URL}/api/order_pdf.php?order_code=${encodeURIComponent(orderCode)}&user_id=${encodeURIComponent(user.id)}`;
          window.open(printUrl, '_blank');
          showMessage('جاري فتح الفاتورة للطباعة...', 'info');
        } else {
          showMessage('بيانات الطلب غير متوفرة', 'error');
        }
        }}
        sx={{
        borderColor: 'primary.main',
        color: 'primary.main',
        fontSize: '1.1rem',
        px: 4,
        py: 2,
        '&:hover': {
          borderColor: 'primary.dark',
          backgroundColor: 'primary.main',
          color: 'white'
        }
        }}
      >
        طباعة الطلب
      </Button>
      </Stack>

      
      
      <Alert severity="info" sx={{ mt: 3, textAlign: 'right', backgroundColor: 'white' }}>
      <Typography variant="body2">
        💡 نصيحة: اكتب كود الطلب في مكان آمن أو خذ لقطة شاشة لهذه الصفحة
      </Typography>
      </Alert>
    </Paper>
    );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderShippingInfo();
      case 1:
        return renderDeliveryMethod();
      case 2:
        return renderCartReview();
      case 3:
        return renderPaymentMethod();
      case 4:
        return renderOrderConfirmation();
      default:
        return null;
    }
  };

  if (orderPlaced && activeStep === 4) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
        {renderOrderConfirmation()}
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
          <BusinessIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#2E7D32', mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2E7D32', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              إتمام طلب الجملة
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              مراجعة وتأكيد طلب الجملة الخاص بك
            </Typography>
          </Box>
        </Box>

        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 4,
            '& .MuiStepConnector-root': {
              flex: { xs: '0 1 8px', sm: '1 1 50px' }
            },
            '& .MuiStep-root': {
              padding: { xs: '0 2px', sm: '0 8px' }
            }
          }} 
          orientation="horizontal"
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel 
                sx={{ 
                  '& .MuiStepLabel-label': { 
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    lineHeight: { xs: 1.2, sm: 1.43 },
                    maxWidth: { xs: '60px', sm: 'none' },
                    textAlign: 'center',
                    wordBreak: 'break-word'
                  },
                  '& .MuiStepLabel-iconContainer': {
                    paddingRight: { xs: '2px', sm: '8px' }
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent(activeStep)}

        {activeStep < 4 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              variant="outlined"
              fullWidth={isMobile}
              sx={{ order: { xs: 2, sm: 1 } }}
            >
              السابق
            </Button>
            <Button
              onClick={activeStep === steps.length - 2 ? handlePlaceOrder : handleNext}
              variant="contained"
              disabled={loading}
              sx={{ 
                backgroundColor: '#2E7D32', 
                minWidth: { xs: 'auto', sm: 120 },
                order: { xs: 1, sm: 2 }
              }}
              fullWidth={isMobile}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                activeStep === steps.length - 2 ? 'تأكيد الطلب' : 'التالي'
              )}
            </Button>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WholesaleCheckout;
